/**
 * @file NarrativeWidget.tsx
 *
 * @description A RJSF widget that provides an interface for editing narrative text
 * with AI-assisted features like condensing, expanding, name correction,
 * and regeneration with additional context.
 *
 * @module Widgets
 */
import { WidgetProps } from "@rjsf/utils";
import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { NarrativeWidgetOptions, LoadingOperation } from "./narrative/types";
import { useNarrativeRegeneration } from "./narrative/useNarrativeRegeneration";
import { NarrativeEditor } from "./narrative/NarrativeEditor";
import { AIAssistanceTools } from "./narrative/AIAssistanceTools";
import {
  NarrativeConfirmationModal,
  OperationType,
} from "./narrative/NarrativeConfirmationModal";
import { CitedNarrative } from "./narrative/CitedNarrative";
import {
  parseCitations,
  stripCitationTokens,
} from "../../features/narrative-citations/parseCitations";
import { useNarrativeCitationsEnabled } from "../../hooks/useNarrativeCitationsEnabled";
import type { CitationGroup } from "../../features/narrative-citations/types";

export const NarrativeWidget = (props: WidgetProps) => {
  const {
    id,
    value,
    label,
    onChange,
    required,
    disabled,
    readonly,
    placeholder,
    schema,
    options,
    registry,
  } = props;

  // Extract custom options from both widget options and formContext
  const widgetOptions = options as NarrativeWidgetOptions;
  const formContext = registry?.formContext as
    | NarrativeWidgetOptions
    | undefined;

  /**
   * Converts RJSF widget id to a JSON path for json_query.
   * Example: "root_Narratives_0_Text" -> "root->Narratives->0->Text"
   */
  const convertIdToJsonPath = (widgetId: string): string => {
    return widgetId.replace("root_", "root→Narrative→").replace(/_/g, "→");
  };

  /**
   * Extracts the narrative index from the RJSF widget id.
   */
  const extractNarrativeIndex = (widgetId: string): number => {
    const match = widgetId.match(/Narratives?_(\d+)/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Convert id to JSON path for agent API
  const narrativePath = convertIdToJsonPath(id);

  const narrativeIndex =
    widgetOptions?.narrativeIndex ??
    formContext?.narrativeIndex ??
    extractNarrativeIndex(id);
  const liveWebSocket =
    widgetOptions?.liveWebSocket ?? formContext?.liveWebSocket;
  const handleUserActivity =
    widgetOptions?.handleUserActivity ?? formContext?.handleUserActivity;
  const handleSaveChanges =
    widgetOptions?.handleSaveChanges ?? formContext?.handleSaveChanges;
  const showError = widgetOptions?.showError ?? formContext?.showError;
  const userProfile = widgetOptions?.userProfile ?? formContext?.userProfile;
  const chatSendMessage = formContext?.chatSendMessage;
  const setShowChat = formContext?.setShowChat;
  const showAIControls = formContext?.showAIControls;

  // Citation anchor click handler, threaded from the page (seeks the evidence
  // player). Optional — the tooltip still shows the source without it.
  const onCite = (registry?.formContext as { onCite?: unknown } | undefined)
    ?.onCite as ((group: CitationGroup) => void) | undefined;
  const { enabled: citationsEnabled } = useNarrativeCitationsEnabled();

  // Decode inline citation tokens. When present (and the flag is on) we show a
  // read-only "cited" preview with clickable transcript/video anchors above the
  // editor. The editor textarea can't render the invisible anchors, so it shows
  // the token-stripped text for readability — but the STORED value must keep its
  // tokens through every save (they are stripped only at the sign/export step).
  const rawValue = typeof value === "string" ? value : "";
  const { groups } = useMemo(() => parseCitations(rawValue), [rawValue]);
  const showCitations = citationsEnabled && groups.length > 0;
  // The editor (a plain textarea) can never render anchors, so it ALWAYS shows
  // token-stripped text — even when the citations flag is off or a token is
  // malformed — so the private-use sentinels never surface as boxes.
  // stripCitationTokens equals the raw value when there are no tokens.
  const editorValue = stripCitationTokens(rawValue);

  // Guard the editor's onChange so the displayed (stripped) text never silently
  // overwrites the token-bearing form value. If the textarea fires onChange with
  // a value identical to what we displayed (re-render / focus / autosave round
  // trip — NOT a real edit), restore the raw token-bearing value so saves keep
  // citations. A genuine edit still flows through (and drops tokens only in the
  // edited region until regeneration re-grounds — a far smaller loss than wiping
  // every citation on a no-op save).
  const handleEditorChange = (next: string | undefined) => {
    if ((next ?? "") === editorValue && rawValue !== editorValue) {
      onChange(rawValue);
      return;
    }
    onChange(next);
  };

  // State
  const [loadingOperation, setLoadingOperation] =
    useState<LoadingOperation>(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    (() => void) | null
  >(null);
  const [pendingOperationType, setPendingOperationType] =
    useState<OperationType>(null);

  // Hook for AI regeneration via chat
  const { handleNarrativeRegeneration } = useNarrativeRegeneration({
    liveWebSocket,
    narrativeIndex,
    narrativePath,
    showError,
    setLoadingOperation,
    chatSendMessage,
    setShowChat,
  });

  const minRows = (options?.rows as number) || 4;

  // Handlers

  const handleMakeLessWordy = () => {
    setPendingOperationType("condense");
    setConfirmationAction(() => async () => {
      setLoadingOperation("condense");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("CONDENSE", null);
      handleUserActivity?.(`Made narrative less wordy`);
    });
    setConfirmationModalOpen(true);
  };

  const handleMakeMoreDetailed = () => {
    setPendingOperationType("expatiate");
    setConfirmationAction(() => async () => {
      setLoadingOperation("expatiate");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("EXPATIATE", null);
      handleUserActivity?.(`Made narrative more detailed`);
    });
    setConfirmationModalOpen(true);
  };

  const handleToFirstPerson = () => {
    setPendingOperationType("toFirstPerson");
    setConfirmationAction(() => async () => {
      setLoadingOperation("toFirstPerson");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("TO_FIRST_PERSON", null);
      handleUserActivity?.(`Converted narrative to first-person perspective`);
    });
    setConfirmationModalOpen(true);
  };

  const handleToThirdPerson = (officerName: string) => {
    setPendingOperationType("toThirdPerson");
    setConfirmationAction(() => async () => {
      setLoadingOperation("toThirdPerson");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("TO_THIRD_PERSON", { officerName });
      handleUserActivity?.(
        `Converted narrative to third-person perspective (${officerName})`,
      );
    });
    setConfirmationModalOpen(true);
  };

  const handleCorrectName = (nameToCorrect: string, correctName: string) => {
    if (!value) return;
    const updatedText = (value as string).replace(
      new RegExp(nameToCorrect.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
      correctName,
    );
    onChange(updatedText);
    handleUserActivity?.(`Corrected name in narrative`);
  };

  const handleRegenerateWithContext = (context: string) => {
    setPendingOperationType("addContext");
    setConfirmationAction(() => async () => {
      setLoadingOperation("addContext");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("ADD_CONTEXT", {
        additionalContext: context,
      });
      handleUserActivity?.(`Regenerated narrative with additional context`);
    });
    setConfirmationModalOpen(true);
  };

  const executeConfirmation = () => {
    if (confirmationAction) {
      confirmationAction();
    }
    setConfirmationModalOpen(false);
    setPendingOperationType(null);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      {showCitations && (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1.5,
            p: 1.5,
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Citations — click a highlighted phrase to view its transcript or
            video source. Removed automatically when the report is exported.
          </Typography>
          <CitedNarrative
            text={rawValue}
            onCite={onCite}
            sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
          />
        </Box>
      )}
      <NarrativeEditor
        id={id}
        value={editorValue}
        onChange={handleEditorChange}
        disabled={disabled || readonly || false}
        readonly={readonly || false}
        loadingOperation={loadingOperation}
        placeholder={placeholder}
        label={label}
        required={required || false}
        minRows={minRows}
        description={schema.description}
      />
      {showAIControls && (
        <AIAssistanceTools
          loadingOperation={loadingOperation}
          disabled={disabled || readonly || false}
          onMakeLessWordy={handleMakeLessWordy}
          onMakeMoreDetailed={handleMakeMoreDetailed}
          onToFirstPerson={handleToFirstPerson}
          onToThirdPerson={handleToThirdPerson}
          onCorrectName={handleCorrectName}
          onRegenerateWithContext={handleRegenerateWithContext}
          userProfile={userProfile}
        />
      )}

      <NarrativeConfirmationModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setPendingOperationType(null);
        }}
        onConfirm={executeConfirmation}
        operationType={pendingOperationType}
      />
    </Box>
  );
};
